// Reusable contact picker modal. Lists the user's synced contacts with a
// search box and a "Create new contact" button at the bottom.
//
// Team mode: when the logged-in user (from global context) passes
// canSearchTeamContacts — Super Admin, Listing/Transaction Coordinators,
// Client Care — the list splits into "My Contacts" and "Team Contacts"
// (every agent's contacts, owner name shown, listed only once a search is
// typed). Picking a team row links the existing contact id; no copy is made.
//
// Props:
//   userId               — current user's id (used to load their contacts)
//   existingCustomerIds  — contact IDs already attached (filtered out of list)
//   onClose              — called when the user dismisses the modal
//   onPick(contactId)    — called when the user clicks a contact row
//   onCreateNew()        — called when the user clicks the create CTA
//   includeDirectory     — also search the shared Team Directory (professionals)
//   onPickDirectory(pro) — called with { name, email, phone } for a directory row
//
import { useEffect, useMemo, useState } from "react";
import { Search, X, UserPlus, Users } from "lucide-react";
import {
  getAllContactsFromFirebase,
  getContactsForUser,
  getTeamDirectory,
} from "../../utils/firebasefunctions";
import { canSearchTeamContacts } from "../../utils/roles";
import { useStateValue } from "../../context/StateProvider";

export default function AddClientPicker({
  userId,
  existingCustomerIds = [],
  onClose,
  onPick,
  onCreateNew,
  includeDirectory = false,
  onPickDirectory,
}) {
  const [{ user }] = useStateValue();
  const teamMode = canSearchTeamContacts(user);
  const [allContacts, setAllContacts] = useState([]);
  const [directoryPros, setDirectoryPros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = teamMode
          ? await getContactsForUser(user)
          : await getAllContactsFromFirebase(userId);
        if (!cancelled) setAllContacts(list || []);
      } catch (err) {
        console.error("Failed to load contacts:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    if (includeDirectory) {
      getTeamDirectory()
        .then((rows) => !cancelled && setDirectoryPros(rows || []))
        .catch(() => {});
    }
    return () => {
      cancelled = true;
    };
  }, [userId, includeDirectory, teamMode, user]);

  const { filtered, teamFiltered } = useMemo(() => {
    const existing = new Set(existingCustomerIds.map(String));
    const q = query.trim().toLowerCase();
    const normalizedQ = q.replace(/\D/g, "");

    const matchesQuery = (c) => {
      if (!q) return true;
      const name = (c.name || "").toLowerCase();
      const email = (c.email || "").toLowerCase();
      const phone = (c.phone || "").replace(/\D/g, "");
      if (normalizedQ && phone.includes(normalizedQ)) return true;
      return name.includes(q) || email.includes(q);
    };

    const byName = (a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, {
        sensitivity: "base",
      });

    const available = allContacts.filter((c) => !existing.has(String(c.id)));

    const mine = available
      .filter((c) => !c._teamOwnerName)
      .filter(matchesQuery)
      .sort(byName)
      .slice(0, 50);

    // Team rows only once the user types — the combined team book can hold
    // tens of thousands of contacts (same rule as the Team Directory below).
    const team = !q
      ? []
      : available
          .filter((c) => c._teamOwnerName)
          .filter(matchesQuery)
          .sort(byName)
          .slice(0, 25);

    return { filtered: mine, teamFiltered: team };
  }, [allContacts, query, existingCustomerIds]);

  // Team Directory matches — only once the user starts typing (the shared
  // account holds thousands of pros; no point listing them unfiltered).
  const filteredDirectory = useMemo(() => {
    if (!includeDirectory) return [];
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const normalizedQ = q.replace(/\D/g, "");
    return directoryPros
      .filter((c) => {
        const name = (c.name || "").toLowerCase();
        const email = (c.email || "").toLowerCase();
        const phone = (c.phone || "").replace(/\D/g, "");
        if (normalizedQ && phone.includes(normalizedQ)) return true;
        return name.includes(q) || email.includes(q);
      })
      .sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        }),
      )
      .slice(0, 15);
  }, [includeDirectory, directoryPros, query]);

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 z-50"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden pointer-events-auto flex flex-col max-h-[80vh]">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
            <div className="text-base font-semibold text-slate-900">
              Add Client
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-700 p-1"
            >
              <X size={18} />
            </button>
          </div>

          <div className="px-5 py-3 border-b border-slate-100">
            <div className="relative">
              <Search
                size={13}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, email, or phone…"
                autoFocus
                className="w-full bg-slate-50 border border-slate-200 focus:bg-white focus:border-slate-400 rounded-md pl-8 pr-3 py-2 text-sm outline-none transition"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="px-5 py-8 text-center text-sm text-slate-500">
                Loading contacts…
              </div>
            ) : filtered.length === 0 &&
              teamFiltered.length === 0 &&
              filteredDirectory.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-slate-500">
                {query
                  ? `No contacts match "${query}"`
                  : "No available contacts. Create a new one below."}
              </div>
            ) : (
              <>
                {teamMode && filtered.length > 0 && (
                  <div className="px-5 py-1.5 bg-slate-50 border-y border-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    My Contacts
                  </div>
                )}
                <ul>
                  {filtered.map((c) => (
                    <li
                      key={c.id}
                      onClick={() => onPick(c.id)}
                      className="px-5 py-2.5 cursor-pointer hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                    >
                      <div className="text-sm font-medium text-slate-900">
                        {c.name || "(no name)"}
                      </div>
                      <div className="text-[11.5px] text-slate-500 flex gap-3 mt-0.5">
                        {c.email && <span>{c.email}</span>}
                        {c.phone && <span>{c.phone}</span>}
                      </div>
                    </li>
                  ))}
                </ul>
                {teamFiltered.length > 0 && (
                  <>
                    <div className="px-5 py-1.5 bg-slate-50 border-y border-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                      <Users size={11} /> Team Contacts
                    </div>
                    <ul>
                      {teamFiltered.map((c) => (
                        <li
                          key={c.id}
                          onClick={() => onPick(c.id)}
                          className="px-5 py-2.5 cursor-pointer hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                        >
                          <div className="text-sm font-medium text-slate-900">
                            {c.name || "(no name)"}
                          </div>
                          <div className="text-[11.5px] text-slate-500 flex gap-3 mt-0.5">
                            {c.email && <span>{c.email}</span>}
                            {c.phone && <span>{c.phone}</span>}
                            <span className="text-slate-400">
                              · {c._teamOwnerName}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                {filteredDirectory.length > 0 && (
                  <>
                    <div className="px-5 py-1.5 bg-slate-50 border-y border-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                      <Users size={11} /> Team Directory
                    </div>
                    <ul>
                      {filteredDirectory.map((c) => (
                        <li
                          key={c.resourceName || c.email || c.name}
                          onClick={() =>
                            onPickDirectory?.({
                              name: c.name || "",
                              email: c.email || "",
                              phone: c.phone || "",
                            })
                          }
                          className="px-5 py-2.5 cursor-pointer hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                        >
                          <div className="text-sm font-medium text-slate-900">
                            {c.name || "(no name)"}
                          </div>
                          <div className="text-[11.5px] text-slate-500 flex gap-3 mt-0.5">
                            {c.email && <span>{c.email}</span>}
                            {c.phone && <span>{c.phone}</span>}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </>
            )}
          </div>

          <div className="border-t border-slate-200 px-5 py-3 bg-slate-50">
            <button
              type="button"
              onClick={onCreateNew}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#1E293B] hover:opacity-90 text-white rounded-md px-3 py-2 text-sm font-semibold transition"
            >
              <UserPlus size={14} /> Create new contact
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
